import { ProgressBar } from "@/components/ui/ProgressBar";
import { MicroLabel } from "@/components/ui/MicroLabel";
import { Button } from "@/components/ui/button";
import { Panel } from "@/components/ui/Panel";
import { Check } from "lucide-react";
import type { Definitions } from "@/lib/element-registry";
import { elementState, elementsOn } from "@/lib/element-registry";
import { tableRows } from "@/lib/figure-registry";
import type { NamedFigure } from "./TitleOffers";
import type { Placement } from "./AnnotatedDrawing";
import { ROW_GRID, RegistryRows } from "./ElementRegistryRows";

/**
 * The table's own header, with the control that confirms the drawing in one go.
 *
 * Confirm all is offered only while something is titled and unconfirmed, which is the only
 * state in which it would do anything: an untitled row is left alone by the rule it follows,
 * and a confirmed one is already where it would put it.
 */
function RegistryHeader({
  anythingToConfirm,
  onConfirmAll,
}: {
  anythingToConfirm: boolean;
  onConfirmAll: () => void;
}) {
  return (
    <header className="flex flex-wrap items-center gap-x-2 gap-y-1.5 border-b border-border px-[14px] py-2.5">
      <MicroLabel className="whitespace-nowrap">
        Elements (shared registry)
      </MicroLabel>
      {/* Auto-detect all is deliberately absent, not merely unwired: it is one
          of the three model-driven features the scoping ruling moved out with
          Suggest and Auto-describe, so it belongs to that story's surface. */}
      <Button
        variant="confirm"
        size="dense"
        type="button"
        onClick={onConfirmAll}
        disabled={!anythingToConfirm}
        className="ml-auto"
      >
        <Check aria-hidden />
        Confirm all
      </Button>
    </header>
  );
}

/** How far this drawing has got, counted in elements rather than in marks. */
function ProgressFooter({
  confirmed,
  total,
}: {
  confirmed: number;
  total: number;
}) {
  return (
    <footer className="flex items-center gap-2.5 border-t border-border px-[14px] py-[9px]">
      <ProgressBar
        value={confirmed}
        max={total}
        tone="success"
        label="Rows confirmed in this table"
        className="flex-1"
      />
      <span className="whitespace-nowrap font-mono text-[11px] text-muted-foreground">
        {`${String(confirmed)} of ${String(total)} confirmed`}
      </span>
    </footer>
  );
}

/**
 * The shared element registry for one drawing.
 *
 * A row per element on this drawing rather than per pin, because an element is one part
 * however many marks carry it, and its title and confirmation belong to the whole set. That
 * is also why editing here says so on every drawing that shares the element.
 */
export function ElementRegistryCard({
  onConfirmAll,
  onNest,
  onTakeOut,
  ...shared
}: {
  definitions: Definitions;
  assigned: Map<string, string>;
  fileId: string;
  figures: NamedFigure[];
  selected: Placement | undefined;
  onRetitle: (elementId: string, title: string) => Promise<void>;
  /** Each title as the store has acknowledged it, so a refused rename never takes the typing back. */
  servedTitles: ReadonlyMap<string, string>;
  onConfirm: (elementId: string) => void;
  onConfirmAll: () => void;
  onReuse: (fromElementId: string, toElementId: string) => void;
  /** Drop one component on another, by drag or by menu. */
  onNest: (dragged: string, target: string) => void;
  onTakeOut: (elementId: string) => void;
}) {
  // The rows take the rest as it came: what they need to draw, name and confirm a component.
  const { definitions, fileId } = shared;
  const ids = elementsOn(definitions, fileId);
  const rows = tableRows(definitions.registry, ids);
  // Counted over the rows the table shows, group rows included, so the footer and Confirm all
  // answer the same question. By the state each row draws rather than the stored flag.
  const states = rows.map((row) => elementState(definitions, row.elementId));
  const confirmed = states.filter((state) => state === "confirmed").length;
  const total = states.length;
  const anythingToConfirm = states.includes("titled");

  return (
    <Panel
      as="section"
      pad="none"
      data-testid="define-element-table"
      // Deliberately unclipped: the offers under a title field are positioned against
      // this card, and a clip here would cut the list off at its edge. No child paints into
      // a corner, so the rounding holds without one.
      className="flex flex-col shadow-sm"
    >
      <RegistryHeader
        anythingToConfirm={anythingToConfirm}
        onConfirmAll={onConfirmAll}
      />

      <div
        className={`${ROW_GRID} border-b border-border bg-muted px-[14px] py-2`}
      >
        <span aria-hidden />
        <MicroLabel>No.</MicroLabel>
        <MicroLabel>Element title</MicroLabel>
      </div>

      {ids.length === 0 ? (
        <p className="m-[14px] text-[11.5px] text-muted-foreground">
          No numerals on this figure yet. Use New numeral, or re-use one the set
          already holds.
        </p>
      ) : (
        <RegistryRows
          {...shared}
          rows={rows}
          onNest={onNest}
          onTakeOut={onTakeOut}
        />
      )}

      <ProgressFooter confirmed={confirmed} total={total} />

      <p className="border-t border-border px-[14px] py-[9px] text-[11.5px] text-muted-foreground">
        An edit to an element reaches every figure it appears on.
      </p>
    </Panel>
  );
}
